"use client";

import { Row } from "@tanstack/react-table";
import { MoreHorizontal, CheckCircle, History, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConciliationItemTypeEnum } from "@/constants/conciliation-history";
import { NsuStatusEnum } from "@/enum/nsu-status";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";

import { NsuDecisionRow } from "../../types";
import {
  CASE_TIMELINE_LABELS,
  NSU_ROW_ACTION_LABELS,
} from "../../utils/messages";
import { CaseTimelineModal } from "../divergence/case-timeline-modal";
import { NsuStatusModal } from "./nsu-status-modal";
import { NSU_HISTORY_ITEM_TEST_ID } from "../../utils/case-history-test-ids";

interface NsuDataTableRowActionsProps<TData extends NsuDecisionRow> {
  row: Row<TData>;
}

export function NsuDataTableRowActions<TData extends NsuDecisionRow>({
  row,
}: NsuDataTableRowActionsProps<TData>): JSX.Element {
  const { handleModal } = useDialogModal();

  const nsu: NsuDecisionRow = row.original;
  const canUpdateStatus: boolean =
    nsu.status === NsuStatusEnum.PENDING || nsu.status === NsuStatusEnum.ACTIVE;

  const handleOpenStatusModal = (): void => {
    handleModal({
      isOpen: true,
      title: NSU_ROW_ACTION_LABELS.STATUS_MODAL_TITLE,
      description: NSU_ROW_ACTION_LABELS.STATUS_MODAL_DESCRIPTION,
      element: <NsuStatusModal nsuId={nsu.id} />,
    });
  };

  const openHistory = (): void => {
    handleModal({
      isOpen: true,
      title: CASE_TIMELINE_LABELS.TITLE,
      description: CASE_TIMELINE_LABELS.DESCRIPTION,
      element: (
        <CaseTimelineModal
          itemType={ConciliationItemTypeEnum.NSU}
          itemId={nsu.id}
        />
      ),
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost_none"
          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted border-none bg-transparent"
        >
          <MoreHorizontal />
          <span className="sr-only">{NSU_ROW_ACTION_LABELS.OPEN_MENU}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        {canUpdateStatus && (
          <DropdownMenuItem
            onClick={handleOpenStatusModal}
            className="cursor-pointer"
          >
            <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
            <span>{NSU_ROW_ACTION_LABELS.DECIDE}</span>
          </DropdownMenuItem>
        )}

        {!canUpdateStatus && (
          <DropdownMenuItem disabled className="text-gray-400">
            <XCircle className="mr-2 h-4 w-4" />
            <span>{NSU_ROW_ACTION_LABELS.ALREADY_DECIDED}</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={openHistory}
          className="cursor-pointer"
          data-testid={NSU_HISTORY_ITEM_TEST_ID}
        >
          <History className="mr-2 h-4 w-4" />
          <span>{NSU_ROW_ACTION_LABELS.HISTORY}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
